// core/services/notificationsService.ts — B4b: API-backed notifications.
//
// list() hydrates the store; byType/byScope/unreadCount stay store-reading;
// markRead/markAllRead POST then patch the store. Signatures + _resetForTest unchanged.

import type { Notification, NotificationType } from '@/core/models/notification';
import { useNotificationStore } from '@/core/stores/notificationStore';
import { useFollowStore } from '@/core/stores/followStore';
import { seedNotifications } from '@/core/data/notifications';
import { api } from '@/core/lib/apiClient';

function getNotifications(): Notification[] {
  return useNotificationStore.getState().notifications;
}

/**
 * A follow_accepted notification means "the account you requested to follow
 * just approved you" — its actor IS the account that was private. Without
 * this, the profile/PostMoreMenu button stays stuck on "Requested" forever
 * after acceptance until a full page reload re-hydrates from the server.
 */
function syncFollowStateFromNotifications(list: Notification[]): void {
  const accepted = list.filter((n) => n.type === 'follow_accepted' && n.actor?.handle);
  if (accepted.length === 0) return;
  const store = useFollowStore.getState();
  for (const n of accepted) {
    const handle = n.actor!.handle;
    store.setFollowing(handle, true);
    store.setRequested(handle, false);
  }
}

export const notificationsService = {
  async list(): Promise<Notification[]> {
    const list = await api<Notification[]>('/notifications/');
    useNotificationStore.getState().setNotifications(list);
    syncFollowStateFromNotifications(list);
    return list;
  },

  async byType(type: NotificationType): Promise<Notification[]> {
    return getNotifications().filter((n) => n.type === type);
  },

  async byScope(scope: 'all' | 'followed'): Promise<Notification[]> {
    if (scope === 'all') return getNotifications();
    return getNotifications().filter((n) => n.fromFollowed === true);
  },

  async markRead(id: string): Promise<void> {
    await api(`/notifications/${id}/read/`, { method: 'POST' });
    useNotificationStore.getState().patchNotification(id, { read: true });
  },

  async markAllRead(): Promise<void> {
    await api('/notifications/mark-all-read/', { method: 'POST' });
    useNotificationStore.getState().markAllRead();
  },

  async unreadCount(): Promise<number> {
    return getNotifications().filter((n) => !n.read).length;
  },

  _resetForTest(): void {
    useNotificationStore.getState().setNotifications(seedNotifications.map((n) => ({ ...n })));
  },
} as const;
